import React, { type JSX } from "react";
import InlineFactBadge from "../InlineFactBadge";
import TableSkeleton from "../TableSkeleton";
import MarkdownTable from "../MarkdownTable";
import ChatMarkdownRenderer from "../ChatMarkdownRenderer";
import { factIdsInText } from "./chatbotConstants";

/**
 * Build a per-message fact index map (1-based, in order of first appearance).
 *
 * For newly streamed assistant text, hallucinated fact markers are filtered server-side,
 * so markers in the streaming text chunks are expected to be valid.
 */
function buildFactIndex(text: string): Record<string, number> {
  const factIdToIndex: Record<string, number> = {};
  let nextIndex = 1;

  for (const factId of factIdsInText(text)) {
    factIdToIndex[factId] = nextIndex++;
  }

  return factIdToIndex;
}

/**
 * Build a document ID -> display index map for THIS MESSAGE ONLY, so badge colors
 * match the source footer. Order follows allDocuments for consistency.
 */
function buildDocumentIndex(
  factIdToIndex: Record<string, number>,
  allFacts: any[],
  allDocuments: any[]
): Record<string, number> {
  const documentIdsInMessage = new Set<string>();

  for (const factId of Object.keys(factIdToIndex)) {
    const fact = allFacts.find((f: any) => f.factId === factId);
    if (fact?.documentId) {
      documentIdsInMessage.add(fact.documentId);
    }
  }

  const documentIdToIndex: Record<string, number> = {};
  allDocuments
    .filter((d: any) => documentIdsInMessage.has(d.documentId))
    .forEach((d: any, idx) => {
      documentIdToIndex[d.documentId] = idx + 1;
    });

  return documentIdToIndex;
}

type BadgeRenderer = (factId: string, key: string) => React.ReactNode;

/**
 * Build the fact-badge callback handed to ChatMarkdownRenderer, so facts render
 * inline within the markdown structure.
 */
function makeBadgeRenderer(
  allFacts: any[],
  factIdToIndex: Record<string, number>,
  documentIdToIndex: Record<string, number>
): BadgeRenderer {
  return (factId, key) => {
    const fact = allFacts.find((f: any) => f.factId === factId);
    // Per-message document index (1-based); default to the first document's colors
    const documentDisplayIndex = fact?.documentId
      ? documentIdToIndex[fact.documentId] || 1
      : null;

    return (
      <InlineFactBadge
        key={key}
        factId={factId}
        fact={fact}
        displayIndex={factIdToIndex[factId]}
        documentDisplayIndex={documentDisplayIndex}
        allFacts={allFacts}
        factIdToIndex={factIdToIndex}
        loading={false}
      />
    );
  };
}

interface Segment {
  type: "text" | "table" | "table_loading";
  content: string;
  blockId?: string;
}

const TABLE_PATTERN = /\[\[(TABLE|TABLE_LOADING):([^\]:]+)(?::([^\]]+))?\]\]/g;

/** Split message text around table placeholders, which need block-level handling. */
function splitTableSegments(text: string): Segment[] {
  const pattern = new RegExp(TABLE_PATTERN.source, "g");
  const segments: Segment[] = [];
  let lastIndex = 0;
  let tableMatch: RegExpExecArray | null;

  while ((tableMatch = pattern.exec(text)) !== null) {
    if (tableMatch.index > lastIndex) {
      segments.push({ type: "text", content: text.substring(lastIndex, tableMatch.index) });
    }

    const [, tableType, blockId, encodedContent] = tableMatch;
    if (tableType === "TABLE") {
      segments.push({
        type: "table",
        content: encodedContent ? decodeURIComponent(encodedContent) : "",
        blockId,
      });
    } else {
      segments.push({ type: "table_loading", content: "", blockId });
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", content: text.substring(lastIndex) });
  }

  return segments;
}

/** Render one segment of a segmented (table-bearing) message. */
function renderSegment(
  segment: Segment,
  index: number,
  renderFactBadge: BadgeRenderer
): JSX.Element {
  if (segment.type === "table") {
    return <MarkdownTable key={`table-${segment.blockId}`} content={segment.content} fadeIn={true} />;
  }
  if (segment.type === "table_loading") {
    return <TableSkeleton key={`table-loading-${segment.blockId}`} />;
  }
  return (
    <ChatMarkdownRenderer
      key={`text-${index}`}
      content={segment.content}
      renderFactBadge={renderFactBadge}
    />
  );
}

/**
 * Render bot message text with inline facts, file links and tables.
 */
export function renderBotMessageWithFacts(
  allFacts: any[],
  allDocuments: any[],
  text: string
): (string | JSX.Element)[] | JSX.Element {
  const factIdToIndex = buildFactIndex(text);
  const documentIdToIndex = buildDocumentIndex(factIdToIndex, allFacts, allDocuments);
  const renderFactBadge = makeBadgeRenderer(allFacts, factIdToIndex, documentIdToIndex);

  // Simple case: no tables - render entire text through markdown with fact callback
  const hasTable = text.includes("[[TABLE:") || text.includes("[[TABLE_LOADING:");
  if (!hasTable) {
    return <ChatMarkdownRenderer content={text} renderFactBadge={renderFactBadge} />;
  }

  return splitTableSegments(text).map((segment, index) =>
    renderSegment(segment, index, renderFactBadge)
  );
}
